/**
 * Contact tracking utilities for modal interactions
 * Tracks phone, email, WhatsApp, and other contact actions
 */

/**
 * Get current language from localStorage or path
 * @returns The current language code (en, de, fr, etc.)
 */
const getCurrentLanguage = (): string => {
  if (typeof window === 'undefined') return 'en';
  
  // Try to get from localStorage first
  const savedLang = localStorage.getItem('preferred-language');
  if (savedLang) return savedLang;
  
  // Fallback to path detection
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const langCode = pathParts[0];
  if (['de', 'fr', 'ja', 'zh', 'ko'].includes(langCode)) return langCode;
  
  return 'en';
};

/**
 * Shared helper for contact tracking
 * Ensures consistent variable clearing and data structure
 * @param eventName - The event name (e.g., 'contact_concierge_phone')
 * @param contactType - Type of contact method (phone, email, whatsapp, location)
 * @param contactValue - The contact value (phone number, email, etc.)
 * @param contactSource - Where contact was initiated (e.g., 'concierge_modal', 'footer_direct')
 * @param additionalData - Additional tracking parameters
 */
const trackContactBase = (
  eventName: string,
  contactType: 'phone' | 'email' | 'whatsapp' | 'location',
  contactValue: string,
  contactSource: string,
  additionalData: Record<string, unknown> = {}
): void => {
  if (typeof window === 'undefined') return;
  
  window.dataLayer = window.dataLayer || [];
  
  // Clear all contact-related variables first (prevents data pollution)
  window.dataLayer.push({
    // Clear contact button tracking variables
    modal_type: undefined,
    button_text: undefined,
    button_location: undefined,
    modal_name: undefined,
    parent_modal: undefined,
    page_location: undefined,
    // Clear contact action variables
    contact_type: undefined,
    contact_source: undefined,
    phone_number: undefined,
    email_address: undefined,
    whatsapp_number: undefined,
    region: undefined,
    destination: undefined,
    booking_method: undefined,
    location: undefined,
    // Clear context-specific variables
    activity_id: undefined,
    activity_name: undefined,
    spa_service_id: undefined,
    spa_service_name: undefined,
    event_type: undefined,
  });
  
  // Build event data
  const eventData: Record<string, unknown> = {
    event: eventName,
    event_category: 'engagement',
    event_label: eventName.replace('contact_', '').replace('activity_', 'activity_'),
    contact_type: contactType,
    contact_source: contactSource,
    user_language: getCurrentLanguage(),
    page_path: window.location.pathname,
    ...additionalData,
  };
  
  // Add contact method value based on type
  if (contactType === 'phone') {
    eventData.phone_number = contactValue;
  } else if (contactType === 'email') {
    eventData.email_address = contactValue;
  } else if (contactType === 'whatsapp') {
    eventData.whatsapp_number = contactValue;
  } else if (contactType === 'location') {
    eventData.location = contactValue;
  }
  
  window.dataLayer.push(eventData);
};

/**
 * Track contact actions from concierge, spa, theatre, event_planning modals
 * @param source - The contact source ('concierge', 'theatre', 'spa', 'event_planning')
 * @param actionType - The type of contact action
 * @param details - Additional tracking parameters (can include custom contact_source)
 */
export const trackContactAction = (
  source: 'concierge' | 'theatre' | 'spa' | 'event_planning',
  actionType: 'phone' | 'email' | 'whatsapp' | 'location' | 'calendly',
  details: Record<string, any> = {}
): void => {
  const contactSource = details.contact_source || `${source}_modal`;
  const contactValue =
    actionType === 'phone'
      ? details.phone_number || '+41 33 841 00 21'
      : actionType === 'email'
        ? details.email_address || 'info@beatenberg-resortspa.ch'
        : actionType === 'whatsapp'
          ? details.whatsapp_number || '+41 76 622 01 22'
          : details.location || '';

  // Only use trackContactBase for non-calendly actions
  if (actionType !== 'calendly') {
    trackContactBase(
      `contact_${source}_${actionType}`,
      actionType === 'calendly' ? 'location' : actionType,
      contactValue,
      contactSource,
      details,
    );
  } else {
    // Handle calendly separately (doesn't fit the standard contact pattern)
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: `contact_${source}_${actionType}`,
        event_category: 'engagement',
        event_label: `${source}_${actionType}`,
        contact_type: actionType,
        contact_source: contactSource,
        user_language: getCurrentLanguage(),
        page_path: window.location.pathname,
        ...details,
      });
    }
  }
};

/**
 * Track activity-specific contact actions from the Activities page (e.g., FAQ contact modal)
 * Uses the activity_contact_{type} naming pattern:
 * - activity_contact_phone
 * - activity_contact_email
 * - activity_contact_whatsapp
 * - activity_contact_location
 *
 * @param actionType - The type of contact action
 * @param details - Additional tracking parameters (can include custom contact_source)
 */
export const trackActivityContactFromModal = (
  actionType: 'phone' | 'email' | 'whatsapp' | 'location',
  details: Record<string, any> = {}
): void => {
  const contactSource = details.contact_source || 'activities_faq_modal';
  const contactValue =
    actionType === 'phone'
      ? details.phone_number || '+41 33 841 00 21'
      : actionType === 'email'
        ? details.email_address || 'info@beatenberg-resortspa.ch'
        : actionType === 'whatsapp'
          ? details.whatsapp_number || '+41 76 622 01 22'
          : details.location || 'parkhotel_beatenberg';

  trackContactBase(
    `activity_contact_${actionType}`,
    actionType,
    contactValue,
    contactSource,
    details,
  );
};
